import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@jem-open/jem-ui";
import { AuditEntry } from "../services/model";

export interface AuditHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteName: string;
  entries: AuditEntry[];
}

/**
 * Read-only configuration audit history for a Site (ENG-4311 User Story 3).
 *
 * Rendered as a centred Dialog (spec drift #12 — the spec called for a Drawer).
 */
export const AuditHistoryDialog: React.FC<AuditHistoryDialogProps> = ({
  open,
  onOpenChange,
  siteName,
  entries,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[640px]">
      <DialogHeader>
        <DialogTitle>Configuration audit history</DialogTitle>
        <DialogDescription>
          Site-level changes and Roster overrides for{" "}
          <strong>{siteName}</strong>. Read-only — changes apply forward only.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-md max-h-[60vh] space-y-md overflow-y-auto">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="rounded-md border border-greyscale-border p-sm"
          >
            <div className="flex items-center justify-between gap-sm">
              <p className="text-sm font-semibold text-greyscale-text-title">
                {entry.field}
              </p>
              <span className="text-xs text-greyscale-text-caption">
                {entry.timestamp}
              </span>
            </div>
            <p className="text-xs text-greyscale-text-caption mt-xxxs">
              by {entry.user}
            </p>
            <p className="text-sm text-greyscale-text-body mt-xs">
              <strong>{entry.oldValue}</strong> →{" "}
              <strong>{entry.newValue}</strong>
            </p>
            {(entry.updatedRosters.length > 0 ||
              entry.skippedRosters.length > 0) && (
              <div className="mt-xs grid gap-xs sm:grid-cols-2 text-xs">
                <div>
                  <p className="font-semibold text-greyscale-text-title">
                    Updated ({entry.updatedRosters.length})
                  </p>
                  {entry.updatedRosters.length === 0 ? (
                    <p className="text-greyscale-text-caption">None.</p>
                  ) : (
                    <ul className="mt-xxxs space-y-xxxs text-greyscale-text-body">
                      {entry.updatedRosters.map((n) => (
                        <li key={n}>· {n}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-greyscale-text-title">
                    Kept custom ({entry.skippedRosters.length})
                  </p>
                  {entry.skippedRosters.length === 0 ? (
                    <p className="text-greyscale-text-caption">None.</p>
                  ) : (
                    <ul className="mt-xxxs space-y-xxxs text-greyscale-text-body">
                      {entry.skippedRosters.map((n) => (
                        <li key={n}>· {n}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export default AuditHistoryDialog;
